import React, { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";

// Custom hooks
import { useApplicationContext } from "../context/ApplicationContext";
import { useAuthentication } from "../hooks/useAuthentication";
import useNavigationMessaging from "../hooks/useNavigationMessaging";
import { useFailedTenants } from "../hooks/useFailedTenants";

// Components
import { Header } from "../components/Header";
import { VerticalNavigation } from "../components/VerticalNavigation";
import { ContentArea } from "../components/ContentArea";
import { TenantConfirmModal } from "../components/TenantConfirmModal";
import InvalidSessionModal from "../components/InvalidSessionModal";
import { createLogger } from '../utils/logging';

import { getAbilitiesForTenant, createDGRoles } from '../api';

const logger = createLogger('Application');

/**
 * Application - Main application component
 *
 * Orchestrates all application state, hooks, and UI components.
 * This is the top-level component that composes the entire application.
 */
export const Application: React.FC = () => {
  // Destructure individual properties from context (NOT entire state object)
  const {
    // State properties we need
    currentTenant,
    pendingTenantId,
    abilitiesLoaded,
    tenantIds,

    // Action creators
    updateTenant: updateTenantDispatch,
    setCurrentTenant,
    setAbilities,
    setAbilitiesLoaded,
    closeModal,
    setHome,
    checkLandingPageAccess,
  } = useApplicationContext();

  // Business logic references
  const pgmParametersRef = useRef<any[]>([]);
  const lastFetchedTenantRef = useRef<string | null>(null);

  const tenantIdsRef = useRef(tenantIds);
  tenantIdsRef.current = tenantIds;

  // UI interaction handlers
  const handleCloseModal = useCallback(() => closeModal(), [closeModal]);

  // ========================================================================
  // TENANT MANAGEMENT
  // ========================================================================

  const updateTenant = useCallback(
    async (tenantId: string) => {
            // Skip if we've already fetched abilities for this tenant
            if (lastFetchedTenantRef.current === tenantId) {
              return;
            }

      updateTenantDispatch(tenantId);
      setCurrentTenant(tenantId);

      createDGRoles(tenantIdsRef.current, tenantId);

      // Fetch abilities for new tenant (will trigger vertical nav filtering)
      try {
        const data = await getAbilitiesForTenant(tenantId);
        const result = data?.abilities;
        if (result && result.actors && result.actors.length > 0) {
          setAbilities(result['all-abilities'] || []);
        }
        // Mark abilities as loaded even if empty/no actors
        setAbilitiesLoaded(true);
        lastFetchedTenantRef.current = tenantId;
      } catch (error) {
        logger.error('Tenant Abilities Load Failed', `Unable to load abilities for tenant ${tenantId}`, { error, tenantId });
        // Mark as loaded even on error so UI doesn't stay in loading state
        lastFetchedTenantRef.current = tenantId;
        setAbilitiesLoaded(true);
      }
    },
    [updateTenantDispatch, setCurrentTenant, setAbilities, setAbilitiesLoaded],
  );

  // ========================================================================
  // BUSINESS LOGIC HOOKS
  // ========================================================================

  // State for invalid session modal
  const [showInvalidSessionModal, setShowInvalidSessionModal] = useState(false);

  // Memoize the invalid session handler to prevent infinite loop
  const handleInvalidSession = useCallback(() => {
    setShowInvalidSessionModal(true);
  }, []);

  // Authentication and session management
  const { validateSessionId, initializeAuth } = useAuthentication({
    onUpdateTenantDispatch: updateTenantDispatch,
    onInvalidSession: handleInvalidSession,
    // Pre-populate lastFetchedTenantRef before setCurrentTenant fires so the second useEffect's
    // updateTenant guard blocks the otherwise-duplicate initial ability fetch.
    // This is called synchronously only on SUCCESS; on error the ref is left unset so the
    // second useEffect can still call updateTenant as an error-recovery retry path.
    onAbilitiesReady: (tenantId) => { lastFetchedTenantRef.current = tenantId; },
  });

  // Navigation messaging (reads context internally)
  useNavigationMessaging();

  // Failed tenants notification
  useFailedTenants();

  // ========================================================================
  // INITIALIZATION
  // ========================================================================

  useEffect(() => {
    // Load configuration files
    const loadPgmParameters = () => {
      const paramConfigFile = "./pgm-parameters.json";
      fetch(paramConfigFile)
        .then((res: any) => res.json())
        .then((data) => {
          (window as any).pgmParameters = data;
          pgmParametersRef.current = data;
        });
    };

    loadPgmParameters();

    // Initialize authentication and tenants
    initializeAuth();

    // Check for page reload and validate session (only on actual reload)
    if (window.performance?.navigation?.type === 1) {
      validateSessionId();
    }

    return () => {};
  }, [initializeAuth, validateSessionId]);
  
  // Fetch abilities when currentTenant changes (tenant switch OR error-recovery retry).
  // On initialization, initializeAuth calls onAbilitiesReady which pre-sets lastFetchedTenantRef
  // before this fires, so updateTenant's own guard blocks the duplicate. On ability-fetch
  // failure, the ref is left unset and this effect retries via updateTenant.
  useEffect(() => {
    if (currentTenant) {
      updateTenant(currentTenant);
    }
  }, [currentTenant, updateTenant]);
  
  // Check landing page access when abilities are loaded
  useEffect(() => {
    if (abilitiesLoaded && currentTenant) {
      checkLandingPageAccess(currentTenant);
    }
  }, [abilitiesLoaded, currentTenant, checkLandingPageAccess]);


  // ========================================================================
  // RENDER
  // ========================================================================

  return (
    <React.Fragment>

      <VerticalNavigation />

      <Header />

      <Box className="app-main">
        <ContentArea />
      </Box>

      <TenantConfirmModal
        onConfirm={() => {
          closeModal();
          setHome();
          updateTenant(pendingTenantId);
        }}
        onCancel={handleCloseModal}
        updateTenant={updateTenant}
      />

      <InvalidSessionModal isOpen={showInvalidSessionModal} />
    </React.Fragment>
  );
};
